(ns rlws.iot-cloud.frontend.common
  (:require [clojure.pprint :as pprint]
            [clojure.string :as str]
            [fork.re-frame :as fork]
            [goog.string :as gstr]
            goog.string.format
            [potpuri.core :as potpuri]
            [reagent.core :as r]
            [re-frame.core :as rf]
            [tick.alpha.api :as t]
            [tick.format :as tf]
            tick.locale-en-us))

(def media-breakpoints
  {:small "320px"
   :medium "600px"
   :large "768px"
   :extra-large "1024px"})

(defn media-query
  "Expects a vector of the form [:max-width :small]"
  [[condition breakpoint]]
  (. (js/window.matchMedia (str "(" (name condition) ": " (media-breakpoints breakpoint) ")"))
     -matches))

(defn search-params
  []
  (new js/URLSearchParams js/window.location.search))

(defn next-url
  []
  (when-let [next (.get (search-params) "next")]
    ;; if param starts with a / then it is an internal link, otherwise it
    ;; is assumed to be a panel and converted to a keyword here to stop us
    ;; redirecting to external sites
    (if (str/starts-with? next "/")
      next
      (keyword next))))

(defn id-key
  "Takes a namespaced keyword (or stringified ns keyword) and returns a string
  of the keyword with the namespace removed. E.g :foo/bar becomes 'bar'"
  [id]
  (some-> id
          str
          keyword
          name))

(defn keyword->string
  "Takes a namespaced keyword and returns a string of the keyword that can be
  turned back into a keyword later"
  [k]
  (some-> k
          str
          (subs 1)))

(def locale js/navigator.language)

(defn twelve-hour-time
  "Takes a time and gives the 12 hour display"
  [t]
  (let [minute (t/minute t)
        hour (t/hour t)]
    (cond
      (= (t/noon) t)
      "12:00 PM"

      (>= hour 13)
      (gstr/format "%2d:%02d PM" (- hour 12) minute)

      (>= hour 12)
      (gstr/format "%2d:%02d PM" hour minute)

      (< hour 12)
      (gstr/format "%2d:%02d AM" hour minute))))

(defn date [t]
  (str
   (t/format (tf/formatter
              (if (= "en-US" locale)
                "MM/dd/YYYY"
                "dd/MM/YYYY"))
             (t/date (t/instant t)))
   " "
   (twelve-hour-time t)))

(defn scroll-navigation
  [target]
  (let [state (atom nil)
        logic
        (fn []
          (let [nav-class target
                nav-node (js/document.querySelector nav-class)
                ;; to get nav offset from top plus nav height. With this, the
                ;; initial position of the content will always be exactly below the
                ;; nav no matter how far from the top. Though this won't work if
                ;; the nav is on the side, so it might be worth to have an option
                ;; to customize it.
                nav-offset (fn [] (+ (.-top (.getBoundingClientRect nav-node))
                                     (.-offsetHeight nav-node)))
                ;; to get all <a> tags from user specified class or id
                link-elements (array-seq
                               (.getElementsByTagName nav-node "a"))
                ;; to add ids from href attributes
                section-attrs (mapv
                               (fn [link-element]
                                 (let [href (.getAttribute link-element "href")]
                                   {:link link-element
                                    :href href
                                    :id (last (str/split href #"#"))}))
                               link-elements)
                ;; to add section nodes from ids. Note that we can grab the
                ;; relevant sections based on the <a> tag hrefs
                section-attrs-elements
                (mapv (fn [attrs]
                        (merge
                         attrs
                         {:node
                          (js/document.getElementById
                           (:id attrs))})) section-attrs)
                ;; callback to highlight the nav elements based on sections
                ;; position
                active-class-listener
                (fn []
                  (let [y-scroll js/window.scrollY
                        visited (filter
                                 (fn [{:keys [node id]}]
                                   (let [;; to compute section distance from nav.
                                         ;; Negative value means content has been/is being viewed.
                                         ;; Zero means content is right below nav.
                                         ;; Positive means content hasn't reached the top yet.
                                         section-offset
                                         (int (- (.-top (.getBoundingClientRect node))
                                                 (nav-offset)))]
                                     (or (zero? section-offset)
                                         (neg? section-offset))))
                                 section-attrs-elements)
                        ;; section currently being viewed. Calling last on the
                        ;; vec is enough as we can rely on the ordering, which
                        ;; means that non-last items have already been viewed.
                        active (or (not-empty (last visited))
                                   (first section-attrs-elements))]
                    ;; to remove active class from all <a> tags
                    (mapv
                     (fn [{:keys [^js link]}]
                       (.remove (.-classList link) "active"))
                     section-attrs-elements)
                    ;; to add active class to active <a> tag
                    ;; TODO (maybe) allow user to specify the element that gets the class
                    (when (:link active)
                      (.add (.-classList (:link active)) "active"))))]

            ;; ONLY SIDE EFFECTS
            ;; to add on click listener to link items
            (mapv
             (fn [{:keys [^js link ^js node]}]
               (.addEventListener
                link "click"
                (fn [e]
                  (.preventDefault e)
                  (js/window.scrollTo #js {:top (- (.-offsetTop node)
                                                   (nav-offset))}))))
             section-attrs-elements)

            ;; run callback on load
            (active-class-listener)
            ;; return listener callback
            active-class-listener))]

    {:scroll-navigation/start #(js/window.addEventListener "scroll" (reset! state (logic)))
     :scroll-navigation/stop #(js/window.removeEventListener "scroll" @state)}))

(def form-template
  {"asset" ["asset" "category" "company" "address"]
   "scale" ["model" "make" "sn" "coc"]
   "gateway-box" ["model" "make" "sn" "firmware" "ip" "mac" "coc"]
   "weight-indicator" ["model" "make" "sn" "firmware" "ip" "mac" "coc"]
   "junction-box" ["model" "make" "sn" "firmware" "ip" "mac" "coc"]
   "remote-display" ["model" "make" "sn" "firmware" "ip" "mac" "coc"]})

(def template-options
  [{:label "Component"
    :value "component"}
   {:label "Scale"
    :value "scale"}])

(defn template-label
  [template-options template-value]
  (first (keep
          #(when (= template-value (:value %))
             (:label %)) template-options)))

(def icons
  {:asset "fas fa-balance-scale"
   :category "fa-layer-group"
   :company "fa-building"
   :address "fa-address-book"
   :model "fa-cubes"
   :make "fa-industry"
   :sn "fa-info"
   :coc "fa-id-badge"
   :firmware "fa-desktop"
   :ip "fa-ethernet"
   :mac "fa-fingerprint"})

(defn auto-form
  [editable-fields {:keys [collection-name custom-components submit-fn]}]
  [fork/form {:path :form
              :form-id collection-name
              :prevent-default? true
              :clean-on-unmount? true
              :on-submit (or submit-fn #(rf/dispatch [:data/update % collection-name]))
              :initial-values (potpuri/map-keys
                               keyword->string
                               editable-fields)}
   (fn [{:keys [values
                form-id
                handle-change
                handle-blur
                submitting?
                handle-submit
                reset] :as props}]
     [:div.form
      [:form.profile-component__form
       {:id form-id
        :on-submit handle-submit}
       (for [input (map keyword->string (keys editable-fields))
             :let [value (get values input)]]
         ^{:key input}
         [:div.margin-bottom-sm
          [:label.form-label.form__label
           (str/capitalize (id-key input))]
          [:div.form-control-wrapper
           (if-let [component (get custom-components (keyword input))]
             [component props]
             [:input.form-control
              {:type "text"
               :name input
               :value value
               :on-change handle-change
               :on-blur handle-blur}])]])
       [:button.btn.btn--md.form__submit
        {:type "submit"
         :disabled submitting?}
        "Submit"]]])])

(defn component-hide-show
  [component & [args]]
  (let [!ref-toggle (atom nil)
        !ref-box (atom nil)
        active? (r/atom false)
        handler (fn [e]
                  (let [^js node (.-target e)]
                    (cond
                      ;; don't close box if click happens on child-box
                      (.contains @!ref-box node) nil
                      ;; to toggle box - show/hide
                      (.contains @!ref-toggle node) (swap! active? not)
                      ;; always close child-box when clicking out
                      :else (reset! active? false))))
        ref-toggle (fn [el] (reset! !ref-toggle el))
        ref-box (fn [el] (reset! !ref-box el))]
    (r/create-class
     {:component-did-mount
      (fn []
        (js/document.addEventListener "mouseup" handler))
      :component-will-unmount
      (fn []
        (js/document.removeEventListener "mouseup" handler))
      :reagent-render
      (fn [component]
        [component @active? ref-toggle ref-box args])})))

(defn debug-filters-selected
  [active-filters]
  (cond
    (empty? active-filters) "All"
    (>= 3 (count active-filters))
    (reduce str (interpose ", " (take 3 active-filters)))
    :else (str (count active-filters) " items selected")))

(defn pprint-str
  [x]
  (with-out-str (pprint/pprint x)))

(defn pprint-code
  "If the app is running in dev environment, renders a pretty printed
  representation of the given object"
  [x]
  (r/with-let [filters (r/atom #{})]
    (let [keywordized (map #(keyword (subs % 1)) @filters)]
      (when (and @(rf/subscribe [:debug?])
                 @(rf/subscribe [:highlight-js-loaded?]))
        [:div.debug-code
         (when (map? x)
           [component-hide-show
            (fn [active? ref-button ref-dropdown]
              [:div.table__dropdown-filter-container
               [:div.flex.items-center
                [:button.btn.btn--md.dropdown-filter__button
                 {:ref ref-button}
                 [:p.dropdown-filter--selected
                  (debug-filters-selected @filters)]
                 [:i.fas.fa-caret-down]]
                [:p.margin-left-sm.font-bold.cursor-pointer
                 {:on-click #(reset! filters #{})}
                 "CLEAR"]]
               (into
                [:div
                 {:ref ref-dropdown
                  :class (if active?
                           "dropdown-filter--show"
                           "dropdown-filter--hide")}]
                (mapv
                 (fn [value]
                   ^{:key (str value)}
                   (let [value (str value)]
                     [:div.dropdown-filter__element
                      {:on-click #(swap! filters
                                         (fn [s]
                                           (if (get @filters value)
                                             (disj s value)
                                             (conj s value))))}
                      [:input.checkbox
                       {:type "checkbox"
                        :checked (get @filters value false)
                        :value value
                        :on-change #()}]
                      [:label value]]))
                 (keys x)))])])
         [:div
          [:pre
           [:code {:dangerouslySetInnerHTML
                   {:__html (.-value
                             (js/hljs.highlight
                              "clojure"
                              (pprint-str
                               (if (and (map? x)
                                        (seq @filters))
                                 (select-keys x keywordized)
                                 x))))}}]]]]))))

(defn format-lbs
  [lbs]
  (if lbs
    (gstr/format "%08d" lbs)
    "-"))

(defn resolve-capability
  [capability]
  (case capability
    (:dia-cell-connection
     :junction-box-disconnected
     :gateway-disconnected) :no-connection
    :emulation :emulation
    :alert))

(defn json->cljs
  [json]
  (js->clj (.parse js/JSON json) :keywordize-keys true))

(defn alert-icon-class [alert]
  (case (resolve-capability alert)
    :emulation "alerts-table__emulation-icon"
    :no-connection "alerts-table__no-connection-icon"
    :alert "alerts-table__alert-icon"
    nil))

(defn remove-internal-keys [m]
  (potpuri/remove-keys #(or (= (name %) "id")
                            (-> % name (.startsWith "_"))) m))

(defn base64-to-image-mime
  [^js/String base64]
  (when base64
    (case (.charAt base64 0)
      "/" "image/jpeg"
      "i" "image/png"
      "R" "image/gif"
      nil)))

(defn parse-int
  [x]
  (let [result (js/parseInt x)]
    (when-not (js/isNaN result)
      result)))

(defn parse-float
  [x]
  (let [result (js/parsefloat x)]
    (when-not (js/isNaN result)
      result)))
